import { Link, useLocation } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useState, useSyncExternalStore } from "react";

import logo from "@/assets/logo.png";
import { env } from "@/env/public-env";
import { useUser } from "@/hooks/users/use-user";
import { cn } from "@/infrastructure/lib/utils";
import { Button } from "@/shared/ui/button";

function subscribeScroll(onChange: () => void) {
  window.addEventListener("scroll", onChange, {
    passive: true,
  });
  return () =>
    window.removeEventListener("scroll", onChange);
}
const subscribeNothing = () => () => undefined;
const getScrollSnapshot = () => window.scrollY > 50;
const getServerScrollSnapshot = () => false;

export function Header() {
  const { user } = useUser();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] =
    useState(false);
  const isHomepage = location.pathname === "/";
  const pastScrollThreshold = useSyncExternalStore(
    isHomepage ? subscribeScroll : subscribeNothing,
    getScrollSnapshot,
    getServerScrollSnapshot
  );

  const scrolled = !isHomepage || pastScrollThreshold;

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const isActivePath = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={cn(
        "fixed top-0 right-0 left-0 z-50 w-full border-b bg-background/95 backdrop-blur transition-transform duration-300",
        scrolled ? "translate-y-0" : "-translate-y-full"
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2">
        {/* Logo & Name */}
        <a
          href="/"
          className="flex items-center gap-2 transition-opacity hover:opacity-80"
        >
          <img
            src={logo}
            alt="vivus Logo"
            className="h-7 w-7"
          />
          <span className="text-2xl font-bold">vivus</span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 text-sm font-medium md:flex">
          <a
            href="/"
            className={cn(
              "relative py-1 transition-colors hover:text-foreground",
              isActivePath("/")
                ? "text-foreground"
                : "text-muted-foreground"
            )}
          >
            Home
            {isActivePath("/") && (
              <div className="absolute right-0 bottom-0 left-0 h-0.5 bg-warning" />
            )}
          </a>
          {env.VITE_WAITLIST_MODE && (
            <a
              href="/waitlist"
              className={cn(
                "relative py-1 transition-colors hover:text-foreground",
                isActivePath("/waitlist")
                  ? "text-foreground"
                  : "text-muted-foreground"
              )}
            >
              Early Access
              {isActivePath("/waitlist") && (
                <div className="absolute right-0 bottom-0 left-0 h-0.5 bg-warning" />
              )}
            </a>
          )}
          <a
            href="/contact"
            className={cn(
              "relative py-1 transition-colors hover:text-foreground",
              isActivePath("/contact")
                ? "text-foreground"
                : "text-muted-foreground"
            )}
          >
            Contact
            {isActivePath("/contact") && (
              <div className="absolute right-0 bottom-0 left-0 h-0.5 bg-warning" />
            )}
          </a>
        </nav>

        {/* Desktop Auth & Mobile Menu Button */}
        <div className="flex items-center gap-3">
          {/* Desktop Auth Button */}
          <div className="hidden md:block">
            {user ? (
              <Link to="/app/dashboard">
                <Button size="sm">To App</Button>
              </Link>
            ) : env.VITE_WAITLIST_MODE ? (
              <Link to="/waitlist">
                <Button size="sm">Get Early Access</Button>
              </Link>
            ) : (
              <Link to="/sign-in">
                <Button size="sm">Sign In</Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="p-2 text-muted-foreground hover:text-foreground md:hidden"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="border-t bg-background/95 backdrop-blur md:hidden">
          <nav className="flex flex-col space-y-4 px-4 py-4">
            <a
              href="/"
              className={cn(
                "relative border-l-4 py-2 text-base font-medium transition-colors hover:text-foreground",
                isActivePath("/")
                  ? "border-warning-border text-foreground"
                  : "border-transparent text-muted-foreground"
              )}
              onClick={() => {
                setMobileMenuOpen(false);
              }}
            >
              <span className="pl-3">Home</span>
            </a>
            {env.VITE_WAITLIST_MODE && (
              <a
                href="/waitlist"
                className={cn(
                  "relative border-l-4 py-2 text-base font-medium transition-colors hover:text-foreground",
                  isActivePath("/waitlist")
                    ? "border-warning-border text-foreground"
                    : "border-transparent text-muted-foreground"
                )}
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
              >
                <span className="pl-3">Early Access</span>
              </a>
            )}
            <a
              href="/contact"
              className={cn(
                "relative border-l-4 py-2 text-base font-medium transition-colors hover:text-foreground",
                isActivePath("/contact")
                  ? "border-warning-border text-foreground"
                  : "border-transparent text-muted-foreground"
              )}
              onClick={() => {
                setMobileMenuOpen(false);
              }}
            >
              <span className="pl-3">Contact</span>
            </a>

            {/* Mobile Auth Button */}
            <div className="border-t pt-4">
              {user ? (
                <Link
                  to="/app/dashboard"
                  onClick={() => {
                    setMobileMenuOpen(false);
                  }}
                >
                  <Button size="sm" className="w-full">
                    To App
                  </Button>
                </Link>
              ) : env.VITE_WAITLIST_MODE ? (
                <Link
                  to="/waitlist"
                  onClick={() => {
                    setMobileMenuOpen(false);
                  }}
                >
                  <Button size="sm" className="w-full">
                    Get Early Access
                  </Button>
                </Link>
              ) : (
                <Link
                  to="/sign-in"
                  onClick={() => {
                    setMobileMenuOpen(false);
                  }}
                >
                  <Button size="sm" className="w-full">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
